import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Home,
  MessageSquare,
  Filter,
  BarChart2,
  TrendingUp,
  Flag,
  Users,
  Settings,
  Moon,
  LogOut,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import useRequest from "../../hook/use-req";
import { UserResponse } from "../../types";
import { toast } from "sonner";
import { cn } from "../../../utils/function";

// Navigation items configuration
const navigationItems = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    path: "/crm/dashboard",
  },
  {
    id: "conversations",
    label: "Conversations",
    icon: MessageSquare,
    path: "/chats",
  },
  {
    id: "leads",
    label: "Leads",
    icon: Filter,
    path: "/leads",
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: BarChart2,
    path: "/analytics",
  },
  {
    id: "activities",
    label: "Activities",
    icon: TrendingUp,
    path: "/activities",
  },
  {
    id: "deals",
    label: "Deals",
    icon: Flag,
    path: "/deals",
  },
  {
    id: "team",
    label: "Team",
    icon: Users,
    path: "/team",
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    path: "/settings",
  },
    {
    id: "convoai",
    label: "ConvoAI",
    icon: MessageSquare,
    path: "/convoAi",
  },
];

const CrmSidebar: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(false);
  const [user, setUser] = useState<UserResponse | null>(null);

  useEffect(() => {
    if (!token) {
      const reloadCount = Number(sessionStorage.getItem("reloadCount") || "0");
      if (reloadCount < 1) {
        sessionStorage.setItem("reloadCount", String(reloadCount + 1));
        window.location.reload();
      } else {
        sessionStorage.removeItem("reloadCount");
      }
    }
  }, [token, navigate]);

  const { makeRequest: userLogOut } = useRequest(`/auth/logout`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const handleSignOut = async () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    navigate("/");

    try {
      const [response] = await userLogOut();

      if (response.status === 200) {
        toast.success(response.message);
        ["campaignId", "shortenedUrl", "uploadedAudience", "validRecords"].forEach((key) =>
          localStorage.removeItem(key)
        );

        setTimeout(() => {
          if (response.redirect_url) {
            window.location.href = response.redirect_url;
          } else {
            window.location.reload();
          }
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      ["campaignId", "shortenedUrl", "uploadedAudience", "validRecords"].forEach((key) =>
        localStorage.removeItem(key)
      );
      navigate("/");
      window.location.reload();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex flex-col h-full m-3 rounded-lg w-[240px] bg-white border-r border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {/* Chat bubble icon */}
          <div className="flex items-center justify-center w-7 h-7">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              className="w-5 h-5 text-gray-700"
            >
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-800 tracking-tight">
            ConvoHub{" "}
            <span className="text-blue-500 font-semibold">CRM</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Refresh */}
          <button
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            onClick={() => window.location.reload()}
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>

          {/* Back arrow */}
          <button
            className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/cpas-dashboard')}
            title="Go back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link key={item.id} to={item.path} className="block">
              <div
                className={cn(
                  "flex items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer select-none",
                  active
                    ? "text-gray-900 font-medium"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                )}
              >
                <Icon
                  className={cn(
                    "w-[18px] h-[18px] shrink-0",
                    active ? "text-gray-800" : "text-gray-400"
                  )}
                  strokeWidth={active ? 2 : 1.75}
                />
                <span className="tracking-tight">{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-gray-100 space-y-0.5">
        {/* Dark Mode Toggle */}
        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3.5">
            <Moon className="w-[18px] h-[18px] text-gray-400 shrink-0" strokeWidth={1.75} />
            <span className="text-sm text-gray-500 tracking-tight">Dark Mode</span>
          </div>

          {/* Toggle Switch */}
          <button
            onClick={() => setDarkMode((prev) => !prev)}
            className={cn(
              "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0",
              darkMode ? "bg-blue-500" : "bg-gray-200"
            )}
            aria-label="Toggle dark mode"
          >
            <span
              className={cn(
                "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200",
                darkMode ? "translate-x-[18px]" : "translate-x-1"
              )}
            />
          </button>
        </div>

        {/* Log Out */}
        <button
          className="flex w-full items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
          onClick={handleSignOut}
        >
          <LogOut className="w-[18px] h-[18px] text-gray-400 shrink-0" strokeWidth={1.75} />
          <span className="tracking-tight">Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default CrmSidebar;